"use client";
import { Button } from "../../components/ui/button";
import { OptimizedImage } from "../../components/common/OptimizedImage";
import {
  MapPin,
  ArrowRight,
  ChevronDown,
  MessageCircle,
  ExternalLink,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { motion } from "motion/react";
import { useAppDispatch, useAppSelector } from "../../_hooks/hooks";
import { getPropertyByURL } from "./services/property.services";
import { getAllCMSByURL } from "../home/services/home.services";
import { createLookupObject, formatPhoneForDisplay, cleanPhoneForTel } from "../../_helper/helper";
import { insertBookingReference } from "../../lib/common";
import { ICMS, ISettings } from "../home/homeModel";
import Image from "next/image";
import dynamic from "next/dynamic";

const Preloader = dynamic(() =>
  import("../../components/Preloader").then((mod) => mod.Preloader),
);
const RoomImageSlider = dynamic(() => import("./sliders/RoomImageSlider"));
const AmenitiesSection = dynamic(() => import("./components/AmenitiesSection"));
const DiningSection = dynamic(() => import("./components/DiningSection"));
const AttractionsSection = dynamic(
  () => import("./components/AttractionsSection"),
);
const IdealSection = dynamic(() => import("./components/IdealSection"));

export function PropertyDetail({
  initialData,
  PropertyURL,
}: {
  initialData?: any;
  PropertyURL?: any;
}) {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const loading = useAppSelector((state: any) => state.property.loading);
  const SettingList = useAppSelector((state: any) => state.home.SettingList);
  const storePropertyDetails = useAppSelector(
    (state: any) => state.property.PropertyDetails,
  );
  const CMSList = useAppSelector((state: any) => state.home.CMSList);
  const cmsUrl = useAppSelector((state: any) => state.home.cmsUrl);

  const PropertyDetails =
    Object.keys(storePropertyDetails || {}).length > 0
      ? storePropertyDetails
      : initialData;

  const fullURL =
    typeof window !== "undefined"
      ? `${window.location.origin}${pathname}`
      : pathname;

  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const sectionRef = useRef(null);
  const [roomCount, setRoomCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc: any, item: any) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const FacebookLink = Settings?.["FacebookLink"]?.SettingValue;
  const InstaLink = Settings?.["InstaLink"]?.SettingValue;
  const XLink = Settings?.["XLink"]?.SettingValue;
  const LinkedInLink = Settings?.["LinkedInLink"]?.SettingValue;
  const YoutubeLink = Settings?.["YoutubeLink"]?.SettingValue;
  const WhatsAppNo = Settings?.["WhatsAppNo"]?.SettingValue;

  const phone = WhatsAppNo ? WhatsAppNo.replace(/\D+/g, "") : "";
  const whatsappLink = `https://wa.me/${phone}?text=${encodeURIComponent("Hello, I’d like to know more details.")}`;

  useEffect(() => {
    if (CMSList && CMSList?.length > 0) {
      setLookUpCMSData(createLookupObject(CMSList, "Name"));
    }
  }, [CMSList]);

  const hydrated = useRef(false);

  // Hydrate store and handle navigation
  useEffect(() => {
    setHasAnimated(false);
    setRoomCount(0);

    const isDifferentProperty =
      storePropertyDetails && storePropertyDetails?.PropertyURL !== PropertyURL;

    if (
      initialData &&
      (Object.keys(storePropertyDetails || {}).length === 0 ||
        initialData?.PropertyURL === PropertyURL) &&
      !hydrated.current
    ) {
      import("./propertySlice").then((mod) => {
        dispatch(mod.setPropertyDetails(initialData));
      });
      hydrated.current = true;
    } else if (isDifferentProperty || !storePropertyDetails?.ID) {
      dispatch(getPropertyByURL(PropertyURL));
    }
  }, [PropertyURL, dispatch, initialData, storePropertyDetails]);

  useEffect(() => {
    if (cmsUrl !== "property") {
      dispatch(getAllCMSByURL("property"));
    }
  }, [cmsUrl, dispatch]);

  useEffect(() => {
    const total = Number(PropertyDetails?.TotalRoom);

    if (!total || total <= 0 || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          setHasAnimated(true);

          let start = 0;
          const duration = 2000;
          const incrementTime = 20;
          const step = Math.ceil(total / (duration / incrementTime));

          const counter = setInterval(() => {
            start += step;
            if (start >= total) {
              start = total;
              clearInterval(counter);
            }
            setRoomCount(start);
          }, incrementTime);

          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [PropertyDetails?.TotalRoom, pathname, hasAnimated]);

  return (
    <>
      {loading && <Preloader />}
      <div className="min-h-screen bg-white text-md">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-end justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <Image
              src={PropertyDetails?.BannerImages || ""}
              alt={PropertyDetails?.PropertyName || "Hero Background"}
              className="object-cover"
              fill
              priority
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80 z-10" />
          </div>

          <div className="container mx-auto px-4  sm:px-6 lg:px-8 relative z-10 mt-25 mb-15">
            <div className="">
              <div>
                <h1 className="text-[#C9A962] text-3xl md:text-4xl lg:text-5xl mb-8 leading-[1.1] tracking-tight text-center font-playfair">
                  {PropertyDetails?.PropertyName}
                </h1>

                <p className="text-white/95 text-xl md:text-2xl text-center font-playfair">
                  {PropertyDetails?.TagLine}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className=" py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                  About This Resort
                </h2>
                <div className="w-24 h-1 bg-[#C9A962] mb-8" />
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="relative group">
                    <div className="overflow-hidden rounded-3xl shadow-xl">
                      <OptimizedImage
                        src={PropertyDetails?.BannerImages}
                        alt="Resort"
                        className="w-full h-[520px] transition duration-700 ease-out group-hover:scale-105"
                        aspectRatio="auto"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 via-emerald-700/10 to-transparent rounded-3xl"></div>
                    </div>

                    {PropertyDetails?.TotalRoom &&
                    +PropertyDetails?.TotalRoom > 0 ? (
                      <div
                        ref={sectionRef}
                        className="absolute -bottom-6 -left-6 bg-[#b42025] text-white w-32 h-32 rounded-full flex flex-col items-center justify-center shadow-2xl border-4 border-white animate-[float_4s_ease-in-out_infinite] hover:bg-[#000] transition-rounded duration-500"
                      >
                        <span className="text-4xl font-serif leading-none">
                          {roomCount}
                        </span>
                        <span className="text-xs tracking-[3px] uppercase mt-1 text-emerald-100">
                          Keys
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <p
                      className="text-gray-700 leading-relaxed space-y-6"
                      dangerouslySetInnerHTML={{
                        __html: PropertyDetails?.LongDescription,
                      }}
                    />

                    {(FacebookLink ||
                      InstaLink ||
                      XLink ||
                      LinkedInLink ||
                      YoutubeLink) && (
                      <div className="border-t border-gray-300 pt-6 mt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <p className="uppercase text-xs tracking-widest text-gray-500 mb-4">
                            Connect With Us On :
                          </p>
                          <div className="flex gap-4">
                            {FacebookLink && (
                              <a
                                href={FacebookLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-black/10 hover:text-white  hover:bg-[#0866ff] flex items-center justify-center transition-colors duration-300"
                              >
                                <Facebook className="h-4 w-4" />
                              </a>
                            )}
                            {InstaLink && (
                              <a
                                href={InstaLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-black/10 hover:text-white hover:bg-[#f90373] flex items-center justify-center transition-colors duration-300"
                              >
                                <Instagram className="h-4 w-4" />
                              </a>
                            )}
                            {XLink && (
                              <a
                                href={XLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-black/10 hover:text-white hover:bg-[#000000] flex items-center justify-center transition-colors duration-300"
                              >
                                <Twitter className="h-4 w-4" />
                              </a>
                            )}
                            {LinkedInLink && (
                              <a
                                href={LinkedInLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-black/10 hover:text-white hover:bg-[#0a66c2] flex items-center justify-center transition-colors duration-300"
                              >
                                <Linkedin className="h-4 w-4" />
                              </a>
                            )}
                            {YoutubeLink && (
                              <a
                                href={YoutubeLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-black/10 hover:text-white hover:bg-[#ff0033] flex items-center justify-center transition-colors duration-300"
                              >
                                <Youtube className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Reservation & Booking Section  */}
        {/* {PropertyDetails?.BookingURL && (
          <section
            className="relative py-24"
            style={{
              backgroundImage: `url('${PropertyDetails?.Propertyimage && PropertyDetails?.Propertyimage?.length > 0 ? PropertyDetails?.Propertyimage[0]?.PropertyGalleryImages : ""}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
            <div className="relative container mx-auto px-6">
              <div className="max-w-5xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-10 text-white shadow-2xl"
                >
                  <div className="text-center mb-10">
                    <h2 className="text-4xl md:text-5xl font-light tracking-wide">
                      Reserve Your Stay
                    </h2>
                    <p className="text-gray-300 mt-3 tracking-widest text-sm uppercase">
                      Experience Luxury & Comfort
                    </p>
                  </div>

                  <div className="flex flex-col md:flex-row justify-between md:items-center gap-8 flex-wrap">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 p-4 rounded-full">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-widest text-gray-300">
                          Call Us
                        </p>
                        <a
                          href={`tel:${PropertyDetails?.MobileNo || ""}`}
                          className="text-xl font-medium"
                        >
                          {PropertyDetails?.MobileNo}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 p-4 rounded-full">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-widest text-gray-300">
                          Email Us
                        </p>
                        <a
                          href={`mailto:${PropertyDetails?.EmailID || ""}`}
                          className="text-xl font-medium break-all"
                        >
                          {PropertyDetails?.EmailID}
                        </a>
                      </div>
                    </div>

                    <a
                      href={PropertyDetails?.BookingURL || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-amber-500 hover:bg-amber-600 text-black px-8 py-4 rounded-full font-semibold tracking-widest uppercase transition duration-300 shadow-lg text-center"
                    >
                      Book Now
                    </a>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        )} */}

        {/* Room Types */}
        {PropertyDetails?.Room && PropertyDetails?.Room?.length > 0 && (
          <section className="py-10 md:py-20 bg-[#F9F7F4]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {lookUpCMSData?.["Resort - Section 1 : Accomodations"] ? (
                <div
                  className="text-center mb-16"
                  dangerouslySetInnerHTML={{
                    __html:
                      lookUpCMSData?.["Resort - Section 1 : Accomodations"]
                        ?.Template,
                  }}
                />
              ) : (
                <div className="text-center mb-20">
                  <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                    Accommodations
                  </p>
                  <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                    Your Private Sanctuary
                  </h2>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
                {PropertyDetails?.Room?.map((room: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white rounded-3xl overflow-hidden shadow-2xl group flex flex-col"
                  >
                    {/* <motion.div
                    key={index}
                    className="bg-white rounded-3xl overflow-hidden shadow-2xl group flex flex-col"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    viewport={{ once: true }}
                  > */}
                    <div className="overflow-hidden">
                      <RoomImageSlider data={room?.Roomimage || []} />
                    </div>
                    <div className="p-10 flex flex-col flex-1">
                      <h3 className="text-3xl text-[#B42025] mb-6 font-Righteous">
                        {room?.RoomTitle}
                      </h3>
                      <p
                        className="text-gray-700 leading-relaxed mb-8 room-section flex-1"
                        dangerouslySetInnerHTML={{
                          __html: room?.RoomDescription,
                        }}
                      />
                      <div>
                        <a
                          href={PropertyDetails?.BookingURL || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            const targetUrl = window.location.href;
                            insertBookingReference(
                              room?.ID || 0,
                              targetUrl,
                            ).catch((err) =>
                              console.error(
                                "Failed to insert booking reference:",
                                err,
                              ),
                            );
                            if (PropertyDetails?.BookingURL) {
                              window.open(
                                PropertyDetails.BookingURL,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }
                          }}
                          className="bg-amber-500 hover:bg-amber-600 text-black px-8 py-4 rounded-full font-semibold tracking-widest uppercase transition duration-300 shadow-lg text-center"
                        >
                          Book Now
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Experiences & Activities */}
        {PropertyDetails?.Activity && PropertyDetails?.Activity?.length > 0 && (
          <section className="py-10 md:py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {lookUpCMSData?.["Resort - Section 2 : Activities"] ? (
                <div
                  className="text-center mb-20"
                  dangerouslySetInnerHTML={{
                    __html:
                      lookUpCMSData?.["Resort - Section 2 : Activities"]
                        ?.Template,
                  }}
                />
              ) : (
                <div className="text-center mb-20">
                  <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                    Activities
                  </p>
                  <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                    Experiences That Define Your Stay
                  </h2>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {PropertyDetails?.Activity?.map(
                  (activity: any, index: number) => (
                    <motion.div
                      key={index}
                      className="bg-[#F9F7F4] rounded-2xl p-10 hover:shadow-2xl transition-all duration-500 group"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className="inline-block p-4 bg-white rounded-full mb-6 transition-colors">
                        <OptimizedImage
                          src={activity?.Image}
                          alt={activity?.Title}
                          className="h-10 w-10 group-hover:scale-120 transition-all duration-500"
                          aspectRatio="1/1"
                        />
                      </div>
                      <h3 className="text-2xl text-[#1A1A1A] mb-4">
                        {activity?.Title}
                      </h3>
                      <p
                        className="text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: activity?.Description,
                        }}
                      />
                    </motion.div>
                  ),
                )}
              </div>
            </div>
          </section>
        )}

        {/* Amenities */}
        {PropertyDetails?.Amenities &&
          PropertyDetails?.Amenities?.length > 0 && (
            <section className="py-10 md:py-20 bg-[#F9F7F4]">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                  {lookUpCMSData?.["Resort - Section 3 : Amenities"] ? (
                    <div
                      className="text-center mb-20"
                      dangerouslySetInnerHTML={{
                        __html:
                          lookUpCMSData?.["Resort - Section 3 : Amenities"]
                            ?.Template,
                      }}
                    />
                  ) : (
                    <div className="text-center mb-20">
                      <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                        Thoughtful Comforts
                      </h2>
                    </div>
                  )}

                  <AmenitiesSection amenities={PropertyDetails?.Amenities} />
                </div>
              </div>
            </section>
          )}

        {/* Dining */}
        {PropertyDetails?.DiningInfo && (
          <section className="py-10 md:py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <DiningSection diningContentHTML={PropertyDetails?.DiningInfo} />
            </div>
          </section>
        )}

        {/* Discover The Region */}
        {PropertyDetails?.Attraction &&
          PropertyDetails?.Attraction?.length > 0 && (
            <section className="py-10 md:py-20 bg-[#1A1A1A] text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div
                  className="w-full h-full"
                  style={{
                    backgroundImage: `url('${
                      lookUpCMSData?.["Resort - Section 4 : Attractions"]
                        ?.CMSImage
                    }')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </div>

              <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {lookUpCMSData?.["Resort - Section 4 : Attractions"] ? (
                  <div
                    className="text-center mb-20"
                    dangerouslySetInnerHTML={{
                      __html:
                        lookUpCMSData?.["Resort - Section 4 : Attractions"]
                          ?.Template,
                    }}
                  />
                ) : (
                  <div className="text-center mb-20">
                    <p className="text-[#C9A962] tracking-[0.3em] mb-4 text-sm uppercase">
                      Explore
                    </p>
                    <h2 className="text-2xl md:text-4xl mb-8">
                      Discover The Region
                    </h2>
                    <div className="w-32 h-1 bg-[#C9A962] mx-auto" />
                  </div>
                )}
                <AttractionsSection attractions={PropertyDetails?.Attraction} />
              </div>
            </section>
          )}

        {/* Perfect For */}
        {PropertyDetails?.Ideal && PropertyDetails?.Ideal?.length > 0 && (
          <section className="py-10 md:py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {lookUpCMSData?.["Resort - Section 5 : Ideal"] ? (
                <div
                  className="text-center mb-20"
                  dangerouslySetInnerHTML={{
                    __html:
                      lookUpCMSData?.["Resort - Section 5 : Ideal"]?.Template,
                  }}
                />
              ) : (
                <div className="text-center mb-20">
                  <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                    Ideal For
                  </p>
                  <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                    Perfect For
                  </h2>
                </div>
              )}

              <IdealSection ideals={PropertyDetails?.Ideal} />
            </div>
          </section>
        )}

        {/* FAQs */}
        {PropertyDetails?.FAQ && PropertyDetails?.FAQ?.length > 0 && (
          <section className="py-10 md:py-20 bg-[#F9F7F4]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                {lookUpCMSData?.["Resort - Section 6 : FAQs"] ? (
                  <div
                    className="text-center mb-20"
                    dangerouslySetInnerHTML={{
                      __html:
                        lookUpCMSData?.["Resort - Section 6 : FAQs"]?.Template,
                    }}
                  />
                ) : (
                  <div className="text-center mb-20">
                    <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                      Frequently Asked Questions
                    </h2>
                  </div>
                )}

                <div className="space-y-6">
                  {PropertyDetails?.FAQ?.map((faq: any, index: number) => (
                    <div
                      key={index}
                      className="bg-white rounded-2xl overflow-hidden shadow-lg"
                    >
                      <button
                        onClick={() =>
                          setOpenFAQ(openFAQ === index ? null : index)
                        }
                        className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-lg text-[#1A1A1A] pr-4">
                          {faq?.Question}
                        </span>
                        <ChevronDown
                          className={`h-6 w-6 text-[#B42025] transition-transform flex-shrink-0 ${openFAQ === index ? "rotate-180" : ""}`}
                        />
                      </button>
                      {openFAQ === index && (
                        <div className="px-8 pb-6">
                          <p
                            className="text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: faq?.Answer }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="py-10 md:py-20 bg-[#1A1A1A] relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url('${PropertyDetails?.BannerImages}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h2 className="text-white text-2xl md:text-4xl mb-8">
              Begin Your Journey
            </h2>
            <p className="text-white/90 text-2xl max-w-3xl mx-auto mb-16">
              Experience the magic of{" "}
              {PropertyDetails?.PropertyName || "beautiful resorts"}. Book your
              stay today.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                href={whatsappLink || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="notranslate"
                translate="no"
              >
                <Button
                  size="lg"
                  className="bg-white text-[#B42025] hover:bg-white/90 px-12 py-7 text-lg"
                >
                  <MessageCircle className="mr-2 h-6 w-6" />
                  WhatsApp Our Travel Expert
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
